import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CandidateBanner from '../components/CandidateBanner';
import Field from '../components/Field';
import RichText from '../components/RichText';
import { DEFAULT_PAGE_COPY, LINK_MESSAGE_KEYS, fillWords, resolvePageCopy } from '../lib/applicationPageCopy';
import { evaluateCondition, getPath, setPath } from '../lib/conditions';
import { usePageIdentity } from '../lib/usePageIdentity';
import { API_BASE } from '../firebase';

/**
 * The application for employment, filled in by somebody outside the firm.
 *
 * OUTSIDE THE AUTH GATE, on the same reasoning as the candidate conflict form: the signed token in
 * the URL is the credential. An applicant has no firm account and never will — the session path
 * deliberately refuses anyone outside @aalrr.com — so requiring a sign-in would make the form
 * impossible to complete rather than more secure.
 *
 * Everything is decided server-side. This page renders whatever `/application/context` says the
 * questions are, and the server re-validates on submit whatever this page believed. It holds no
 * schema of its own, so the firm can reword a question without a web deploy.
 *
 * WHY THE SAVING MATTERS HERE MORE THAN ANYWHERE. This asks for ten years of employment history,
 * three references and three signatures. Nobody completes it in one sitting, and a form that loses
 * your work is a form that never comes back — which is the whole reason the firm wanted a link
 * rather than an open page.
 */

/**
 * What a dead link says, in the firm's wording (pageCopy, sent with the refusal). Every message on
 * this page is the firm's to edit at /apply/form except "Opening your application…", which shows
 * before the wording has arrived.
 */
const linkMessage = (copy, reason) => copy[LINK_MESSAGE_KEYS[reason]] || copy.linkFailed;

/**
 * How much of a line a question takes (`width`, set per question in the editor). Six columns so a
 * half and a third both divide it; on a phone every question is full width. Written out whole so
 * Tailwind sees the classes.
 */
export const WIDTH_CLASS = {
  full: 'col-span-6',
  half: 'col-span-6 sm:col-span-3',
  third: 'col-span-6 sm:col-span-2',
};
export const widthClass = (field) => WIDTH_CLASS[field.width] || WIDTH_CLASS.full;

/**
 * A table opens with one blank entry to type into, and its dropdowns are as tall as its text boxes
 * (Repeatable's `startWithOne` and `evenBoxes`).
 */
export const openTable = (field) =>
  field.type === 'repeatable' ? { ...field, startWithOne: true, evenBoxes: true } : field;

/**
 * A tick box beside its statement, the whole statement clickable, rather than a statement with a
 * small box somewhere under it.
 */
export function TickBox({ field, answers, onChange, disabled, problem }) {
  const required = field.requirement === 'required' || field.mustBeTrue === true;
  return (
    <div>
      <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700 dark:text-slate-200">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 accent-firm-700 dark:accent-firm-400"
          checked={getPath(answers, field.path) === true}
          disabled={disabled}
          onChange={(e) => onChange(setPath(answers, field.path, e.target.checked))}
        />
        <span>
          {field.label}
          {required && <span className="text-red-600 dark:text-red-400"> *</span>}
        </span>
      </label>
      {field.helpText && (
        <div className="mt-1 pl-7 text-xs text-slate-500 dark:text-slate-400">
          <RichText>{field.helpText}</RichText>
        </div>
      )}
      {problem && <p className="mt-1 pl-7 text-xs text-red-600 dark:text-red-400">{problem.message}</p>}
    </div>
  );
}

async function postJson(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let parsed = {};
  try {
    parsed = await res.json();
  } catch {
    parsed = {};
  }
  return { res, body: parsed };
}

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && !value.trim()) ||
  (Array.isArray(value) && value.length === 0);

/** A stored answer shown as a sentence, for the locked fields the firm filled in. */
const readable = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) return value.join(', ');
  return value === undefined || value === null ? '' : String(value);
};

export default function ApplicationForm() {
  const [params] = useSearchParams();
  const formId = params.get('form');
  const token = params.get('token');

  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState(null);
  const [sections, setSections] = useState([]);
  // The page's own words (the box at the top, the notes, the buttons and every message), from
  // settings/employmentApplication.pageCopy, editable on the live form editor.
  const [pageCopy, setPageCopy] = useState(() => resolvePageCopy({}));

  usePageIdentity({ title: pageCopy.tabTitle, icon: '/ccc-favicon.svg' });
  const [answers, setAnswers] = useState({});
  const [bypasses, setBypasses] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [blocking, setBlocking] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Asked even without a token: the refusal carries the firm's wording for it.
      const { res, body } = await postJson('/application/context', { form: formId || '', token: token || '' });
      if (cancelled) return;
      if (!res.ok) {
        const copy = resolvePageCopy(body.pageCopy);
        setPageCopy(copy);
        setLinkError(linkMessage(copy, body.reason || (!formId || !token ? 'missing_token' : '')));
        setLoading(false);
        return;
      }
      setSections(body.sections || []);
      setPageCopy(resolvePageCopy(body.pageCopy));
      setAnswers((body.form && body.form.answers) || {});
      setBypasses((body.form && body.form.bypasses) || {});
      setSubmitted((body.form && body.form.status) === 'submitted');
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, token]);

  const pending = useRef(null);
  const latest = useRef({ answers, bypasses });
  latest.current = { answers, bypasses };

  const save = useCallback(
    async ({ quiet } = {}) => {
      if (submitted) return;
      if (!quiet) setSaving(true);
      const { res, body } = await postJson('/application/save', {
        form: formId,
        token,
        answers: latest.current.answers,
        bypasses: latest.current.bypasses,
      });
      setSaving(false);
      if (res.ok) setSavedAt(body.savedAt || new Date().toISOString());
    },
    [formId, token, submitted]
  );

  const touch = useCallback(() => {
    if (submitted) return;
    if (pending.current) clearTimeout(pending.current);
    pending.current = setTimeout(() => save({ quiet: true }), 2500);
  }, [save, submitted]);

  useEffect(() => () => pending.current && clearTimeout(pending.current), []);

  /**
   * `Field` emits the WHOLE next answers object, not the edited value — it applies the path
   * itself. Treating the argument as the field's value and setting it AT the path again is what
   * put "[object Object]" into the candidate form's one editable header field.
   */
  const change = (nextAnswers) => {
    setAnswers(nextAnswers);
    touch();
  };

  const visibleSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        fields: (section.fields || []).filter(
          (field) => field.visibleIf === undefined || evaluateCondition(field.visibleIf, answers)
        ),
      })),
    [sections, answers]
  );

  const problemFor = (path) => blocking.find((p) => p.path === path);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    if (pending.current) clearTimeout(pending.current);
    const { res, body } = await postJson('/application/submit', {
      form: formId,
      token,
      answers,
      bypasses,
    });
    setSubmitting(false);
    if (res.ok) {
      setBlocking([]);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (body.error === 'incomplete') {
      setBlocking(body.blocking || []);
      const first = (body.blocking || [])[0];
      if (first) {
        const node = document.getElementById(`field-${first.path}`);
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setSubmitError(body.reason === 'already_submitted' ? pageCopy.alreadySubmitted : pageCopy.submitFailed);
  };

  const pdfUrl = `${API_BASE}/application/pdf?form=${encodeURIComponent(formId || '')}&token=${encodeURIComponent(
    token || ''
  )}`;

  if (loading) {
    return <Frame><p className="text-slate-500 dark:text-slate-400">Opening your application…</p></Frame>;
  }
  if (linkError) {
    return (
      <Frame>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          {linkError}
        </div>
      </Frame>
    );
  }

  return (
    <Frame title={pageCopy.title}>
      {submitted ? (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100">
          <p className="font-medium">{pageCopy.thankYou}</p>
          {pageCopy.thankYouDetail && <p className="mt-1">{pageCopy.thankYouDetail}</p>}
          {pageCopy.downloadLabel && (
            <a
              href={pdfUrl}
              className="mt-3 inline-block rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
            >
              {pageCopy.downloadLabel}
            </a>
          )}
        </div>
      ) : (
        pageCopy.intro && (
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
            <RichText>{pageCopy.intro}</RichText>
          </div>
        )
      )}

      {blocking.length > 0 && !submitted && (
        <div className="mt-4 rounded-lg border border-red-400 bg-red-50 p-4 text-sm text-red-900 dark:border-red-600 dark:bg-red-950 dark:text-red-100">
          <p className="font-medium">
            {blocking.length === 1 ? pageCopy.attentionOne : fillWords(pageCopy.attentionMany, { count: blocking.length })}
          </p>
          <ul className="mt-2 list-disc pl-5">
            {blocking.map((problem) => (
              <li key={`${problem.path}:${problem.row ?? ""}:${problem.column ?? ""}`}>{problem.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={submitted ? 'pointer-events-none opacity-70' : ''}>
        {visibleSections.map((section) => (
          <section key={section.id} className="mt-6">
            <h2 className="text-base font-semibold text-ink dark:text-slate-100">{section.title}</h2>
            {section.description && (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{section.description}</p>
            )}
            {/*
              The self-identification section says on the page what it says on paper: voluntary,
              and kept apart from the application. Somebody deciding whether to answer should not
              have to take that on trust from a sentence in a privacy notice two screens down.
            */}
            {section.separateDocument &&
              (section.separateDocument === 'privacyNotice' ? pageCopy.privacyNote : pageCopy.voluntaryNote) && (
              <p className="mt-2 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <RichText>
                  {section.separateDocument === 'privacyNotice' ? pageCopy.privacyNote : pageCopy.voluntaryNote}
                </RichText>
              </p>
            )}
            <div className="mt-3 grid grid-cols-6 gap-x-4 gap-y-4">
              {section.fields.map((field) => (
                <div key={field.path} id={`field-${field.path}`} className={widthClass(field)}>
                  {field.type === 'ccpaNotice' ? (
                    <PrivacyNotice loadingText={pageCopy.noticeLoading} />
                  ) : field.type === 'checkbox' ? (
                    <TickBox field={field} answers={answers} onChange={change} disabled={submitted} problem={problemFor(field.path)} />
                  ) : field.locked === true ? (
                    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
                      <div className="text-xs text-slate-500 dark:text-slate-400">{field.label}</div>
                      <div className="text-[15px] text-ink dark:text-slate-100">
                        {readable(getPath(answers, field.path)) || (
                          <span className="text-slate-400 dark:text-slate-500">{pageCopy.notGiven}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Field
                      // Help text is stored as `helpText` but Field shows `help`: without this the
                      // application's help text never appeared on the form at all.
                      field={{
                        ...openTable(field),
                        ...(field.helpText ? { help: <RichText>{field.helpText}</RichText> } : null),
                        clearLabel: pageCopy.clearChoice,
                      }}
                      matter={answers}
                      config={{}}
                      onChange={change}
                      disabled={submitted}
                      problem={problemFor(field.path)}
                    />
                  )}
                  {field.requirement === 'soft_required' &&
                    isBlank(getPath(answers, field.path)) &&
                    !submitted && (
                      <label className="mt-2 block text-xs text-slate-600 dark:text-slate-300">
                        {pageCopy.reasonPrompt}
                        <textarea
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                          rows={2}
                          value={bypasses[field.path] || ''}
                          onChange={(e) => {
                            setBypasses((prev) => ({ ...prev, [field.path]: e.target.value }));
                            touch();
                          }}
                        />
                      </label>
                    )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {!submitted && (
        <div className="sticky bottom-0 mt-8 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-page/95 py-4 backdrop-blur dark:border-slate-700">
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="rounded bg-firm-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
          >
            {submitting ? pageCopy.sendingLabel : pageCopy.submitLabel}
          </button>
          <button
            type="button"
            onClick={() => save()}
            disabled={saving}
            className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-firm-500 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
          >
            {saving ? pageCopy.savingLabel : pageCopy.saveLabel}
          </button>
          {pageCopy.previewLabel && (
            <a href={pdfUrl} className="text-sm text-firm-700 underline dark:text-firm-300">
              {pageCopy.previewLabel}
            </a>
          )}
          {savedAt && pageCopy.savedLabel && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {fillWords(pageCopy.savedLabel, { time: new Date(savedAt).toLocaleTimeString() })}
            </span>
          )}
          {submitError && (
            <span role="alert" className="text-sm text-red-600 dark:text-red-400">
              {submitError}
            </span>
          )}
        </div>
      )}
    </Frame>
  );
}

/**
 * The privacy notice, rendered from what the server sent rather than from a copy in this bundle.
 *
 * The applicant signs "I have received, read, and understood the above Privacy Notice", so the
 * notice has to BE above it — a link to a PDF nobody opened would make that sentence untrue. It
 * scrolls in place rather than pushing the signature three screens down.
 */
export function PrivacyNotice({ draft, loadingText = DEFAULT_PAGE_COPY.noticeLoading } = {}) {
  const [fetched, setNotice] = useState(null);
  // The live editor passes the notice it is editing; the applicant's page reads the one in force.
  const notice = draft || fetched;

  useEffect(() => {
    if (draft) return undefined;
    let cancelled = false;
    (async () => {
      const res = await fetch(`${API_BASE}/application/notice`);
      if (!res.ok) return;
      const body = await res.json();
      if (!cancelled) setNotice(body);
    })();
    return () => {
      cancelled = true;
    };
  }, [draft]);

  if (!notice) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">{loadingText}</p>
    );
  }

  return (
    <div className="rounded-md border border-slate-300 dark:border-slate-600">
      <div className="max-h-96 overflow-y-auto p-4 text-xs leading-relaxed text-slate-700 dark:text-slate-200">
        <h3 className="text-sm font-semibold text-ink dark:text-slate-100">{notice.title}</h3>
        {(notice.blocks || []).map((block, index) =>
          block.type === 'heading' ? (
            <h4 key={index} className="mt-4 font-semibold text-ink dark:text-slate-100">
              {block.text}
            </h4>
          ) : block.type === 'bullet' ? (
            <p key={index} className="mt-2 pl-4">
              • {block.text}
            </p>
          ) : (
            <p key={index} className="mt-2">
              {block.text}
            </p>
          )
        )}
        <p className="mt-4 italic">Last modified: {notice.lastModified}</p>
      </div>
    </div>
  );
}

function Frame({ children, title = DEFAULT_PAGE_COPY.title }) {
  return (
    <div className="min-h-screen bg-page dark:bg-slate-950">
      <CandidateBanner title={title} width="max-w-5xl" logo />
      <div className="mx-auto max-w-5xl px-4 py-8">{children}</div>
    </div>
  );
}
